/**
 * Zustands- und Aufbereitungslogik der Karte „OAuth-Zugang", bewusst ohne DOM.
 *
 * Das Frontend-Vitest laeuft mit environment: 'node', testbare Logik gehoert
 * deshalb hierher und nicht in die View. Gleiches Muster wie mcp-token-flow.ts.
 *
 * Der Zwei-Klick-Widerruf kommt aus mcp-token-flow.ts und wird NICHT kopiert:
 * Eine zweite Fassung waere eine zweite Wahrheit fuer eine Regel, deren Bruch
 * am 2026-08-07 fast das falsche Token widerrufen haette.
 */

export interface OauthAccountRow {
  id: string;
  name: string;
  secret_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface OauthConnectionRow {
  id: string;
  client_name: string | null;
  scope: string;
  created_at: string;
  access_expires_at: string;
  revoked_at: string | null;
}

export interface FormattedOauthAccount {
  vorhanden: boolean;
  widerrufen: boolean;
  zustandText: string;
  erstelltText: string;
  letzteNutzungText: string;
  row: OauthAccountRow | null;
}

export interface FormattedConnection extends OauthConnectionRow {
  anzeigeName: string;
  scopeText: string;
  zustandText: string;
  erstelltText: string;
}

/** Datum menschenlesbar, ohne bei Muell NaN zu zeigen. */
function datumText(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE');
}

export function formatOauthAccount(row: OauthAccountRow | null): FormattedOauthAccount {
  if (!row) {
    return {
      vorhanden: false,
      widerrufen: false,
      zustandText: 'Noch kein Zugang angelegt',
      erstelltText: '—',
      letzteNutzungText: '—',
      row: null,
    };
  }
  const widerrufen = row.revoked_at !== null;
  return {
    vorhanden: true,
    widerrufen,
    zustandText: widerrufen ? 'Widerrufen' : 'Aktiv',
    erstelltText: datumText(row.created_at),
    letzteNutzungText: row.last_used_at ? datumText(row.last_used_at) : 'Noch nie verwendet',
    row,
  };
}

export function scopeLabel(scope: string): string {
  const teile = scope.split(/\s+/).filter((t) => t !== '');
  const hatSchreiben = teile.includes('kaufland:write');
  const hatAufschalten = teile.includes('kaufland:publish');
  const nurBekannte = teile.every(
    (t) => t === 'kaufland:read' || t === 'kaufland:write' || t === 'kaufland:publish',
  );
  // Unbekanntes unveraendert durchreichen statt zu verschlucken: Eine leere oder
  // erfundene Beschriftung waere schlimmer als der rohe Wert. Deckt auch die 85
  // Altzeilen mit nacktem Einzelwert ab, weil die aus genau einem bekannten
  // Token bestehen und deshalb `nurBekannte` erfuellen.
  if (!nurBekannte || teile.length === 0) return scope;
  if (hatAufschalten) return 'Lesen, schreiben und aufschalten';
  if (hatSchreiben) return 'Lesen und schreiben';
  return 'Nur lesen';
}

export function formatConnections(rows: OauthConnectionRow[]): FormattedConnection[] {
  const jetzt = Date.now();
  return [...rows]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .map((r) => {
      const abgelaufen = new Date(r.access_expires_at).getTime() < jetzt;
      return {
        ...r,
        anzeigeName:
          r.client_name && r.client_name.trim() !== '' ? r.client_name : 'Unbenannter Client',
        scopeText: scopeLabel(r.scope),
        // Widerrufen schlaegt abgelaufen: Ein widerrufenes Token ist endgueltig,
        // ein abgelaufenes koennte per Refresh wiederkommen.
        zustandText: r.revoked_at !== null ? 'Widerrufen' : abgelaufen ? 'Abgelaufen' : 'Aktiv',
        erstelltText: datumText(r.created_at),
      };
    });
}

/** Gleiche Grenzen wie die RPC, damit der Fehler im Formular auffaellt und nicht im Backend. */
export function validateAccountName(name: string): string | null {
  const n = name.trim();
  if (n === '') return 'Bitte einen Namen eingeben.';
  if (n.length > 60) return 'Der Name darf höchstens 60 Zeichen haben.';
  return null;
}
